/**
 * Утилиты для обработки URL файлов и аватаров
 */

/**
 * Проверяет, является ли URL абсолютным (начинается с http/https)
 */
export function isAbsoluteUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return url.startsWith('http://') || url.startsWith('https://');
}

/**
 * Проверяет, является ли URL относительным путем к uploads
 */
export function isRelativeUploadsPath(url) {
  if (!url || typeof url !== 'string') return false;
  return url.startsWith('/uploads') || url.startsWith('uploads');
}

/**
 * Нормализует URL файла для отображения
 * @param {string} fileUrl - URL или путь к файлу
 * @param {string} baseApiUrl - Базовый URL API (для относительных путей)
 * @returns {string} - Готовый URL для отображения
 */
export function normalizeFileUrl(fileUrl, baseApiUrl = '') {
  if (!fileUrl || typeof fileUrl !== 'string') {
    return '';
  }

  // Если URL уже абсолютный (S3), возвращаем как есть
  if (isAbsoluteUrl(fileUrl)) {
    return fileUrl;
  }

  // Если это относительный путь к uploads, формируем полный URL
  if (isRelativeUploadsPath(fileUrl)) {
    // Убираем лишние слэши и формируем URL
    const cleanPath = fileUrl.replace(/^\/+/, '').replace('/uploads', 'uploads');
    const cleanBaseUrl = baseApiUrl.replace(/\/+$/, '');
    return `${cleanBaseUrl}/${cleanPath}`;
  }

  // Для других случаев просто возвращаем исходный URL
  return fileUrl;
}

/**
 * Получает URL аватара с правильной обработкой
 * @param {string} avatarUrl - URL аватара из API
 * @param {string} baseApiUrl - Базовый URL API
 * @returns {string} - Готовый URL для отображения
 */
export function getAvatarUrl(avatarUrl, baseApiUrl = '') {
  return normalizeFileUrl(avatarUrl, baseApiUrl);
}

/**
 * Проверяет, является ли URL файлом изображения по расширению
 * @param {string} url - URL файла
 * @returns {boolean}
 */
export function isImageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
  const urlLower = url.toLowerCase();
  
  return imageExtensions.some(ext => urlLower.includes(ext));
}

/**
 * Извлекает имя файла из URL
 * @param {string} url - URL файла
 * @returns {string} - Имя файла
 */
export function getFileNameFromUrl(url) {
  if (!url || typeof url !== 'string') return '';
  
  try {
    // Для S3 URL извлекаем имя из пути
    if (isAbsoluteUrl(url)) {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      return pathParts[pathParts.length - 1] || '';
    }
    
    // Для относительных путей
    const pathParts = url.split('/');
    return pathParts[pathParts.length - 1] || '';
  } catch (error) {
    console.warn('Error extracting filename from URL:', url, error);
    return '';
  }
}

/**
 * Проверяет доступность изображения
 * @param {string} url - URL изображения
 * @returns {Promise<boolean>}
 */
export function checkImageAvailability(url) {
  return new Promise((resolve) => {
    if (!url) {
      resolve(false);
      return;
    }
    
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
    
    // Timeout после 5 секунд
    setTimeout(() => resolve(false), 5000);
  });
}